import { FirebaseError } from 'firebase/app';

export class FirebaseApiError extends Error {
  public code: string;
  public isRetryable: boolean;
  public userMessage: string;

  constructor(error: FirebaseError) {
    super(error.message);
    this.name = 'FirebaseApiError';
    this.code = error.code;
    this.isRetryable = this.checkIfRetryable(error.code);
    this.userMessage = this.getUserFriendlyMessage(error.code);
  }

  private checkIfRetryable(code: string): boolean {
    const retryableCodes = [
      'unavailable',
      'deadline-exceeded',
      'resource-exhausted',
      'aborted',
      'internal',
      'network-request-failed'
    ];
    
    return retryableCodes.some(retryableCode => 
      code.includes(retryableCode)
    );
  }

  private getUserFriendlyMessage(code: string): string {
    const errorMessages: Record<string, string> = {
      // Auth errors
      'auth/invalid-email': 'Geçersiz e-posta adresi.',
      'auth/user-disabled': 'Bu kullanıcı hesabı devre dışı bırakılmış.',
      'auth/user-not-found': 'Kullanıcı bulunamadı.',
      'auth/wrong-password': 'Hatalı şifre.',
      'auth/email-already-in-use': 'Bu e-posta adresi zaten kullanımda.',
      'auth/weak-password': 'Şifre çok zayıf.',
      'auth/operation-not-allowed': 'Bu işlem şu anda kullanılamıyor.',
      'auth/requires-recent-login': 'Bu işlem için yeniden giriş yapmanız gerekiyor.',
      'auth/too-many-requests': 'Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin.',
      'auth/network-request-failed': 'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.',
      
      // Firestore errors
      'permission-denied': 'Bu işlem için yetkiniz yok.',
      'not-found': 'İstenen veri bulunamadı.',
      'already-exists': 'Bu veri zaten mevcut.',
      'resource-exhausted': 'Kota sınırına ulaşıldı. Lütfen daha sonra tekrar deneyin.',
      'failed-precondition': 'İşlem şartları sağlanamadı.',
      'aborted': 'İşlem iptal edildi.',
      'out-of-range': 'Geçersiz veri aralığı.',
      'unimplemented': 'Bu özellik henüz kullanılamıyor.',
      'internal': 'Sunucu hatası oluştu.',
      'unavailable': 'Servis geçici olarak kullanılamıyor.',
      'data-loss': 'Veri kaybı oluştu.',
      'unauthenticated': 'Kimlik doğrulama gerekli.',
      'deadline-exceeded': 'İşlem zaman aşımına uğradı.',
      'cancelled': 'İşlem iptal edildi.',
      
      // Storage errors
      'storage/object-not-found': 'Dosya bulunamadı.',
      'storage/bucket-not-found': 'Depolama alanı bulunamadı.',
      'storage/project-not-found': 'Proje bulunamadı.',
      'storage/quota-exceeded': 'Depolama kotası aşıldı.',
      'storage/unauthenticated': 'Bu dosyaya erişim için giriş yapmanız gerekiyor.',
      'storage/unauthorized': 'Bu dosyaya erişim izniniz yok.',
      'storage/retry-limit-exceeded': 'Maksimum deneme sayısı aşıldı.',
      'storage/invalid-checksum': 'Dosya bozuk veya eksik.',
      'storage/canceled': 'Yükleme iptal edildi.',
      'storage/invalid-url': 'Geçersiz dosya URL\'i.',
      'storage/cannot-slice-blob': 'Dosya işlenemiyor.',
      'storage/server-file-wrong-size': 'Dosya boyutu uyuşmuyor.'
    };

    // Try to find exact match
    if (errorMessages[code]) {
      return errorMessages[code];
    }

    // Try to find partial match
    for (const [key, message] of Object.entries(errorMessages)) {
      if (code.includes(key)) {
        return message;
      }
    }

    // Default message
    return 'Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
  }
}

// Firebase error handler wrapper
export function handleFirebaseError(
  error: any,
  context?: string
): FirebaseApiError {
  let firebaseError: FirebaseApiError;

  if (error instanceof FirebaseError) {
    firebaseError = new FirebaseApiError(error);
  } else {
    // Create a generic Firebase error
    const genericError = new FirebaseError(
      'unknown',
      error?.message || 'Bilinmeyen Firebase hatası'
    );
    firebaseError = new FirebaseApiError(genericError);
  }

  // Log error in development
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔥 Firebase Error${context ? ` - ${context}` : ''}`);
    console.error('Code:', firebaseError.code);
    console.error('Message:', firebaseError.message);
    console.error('User Message:', firebaseError.userMessage);
    console.error('Retryable:', firebaseError.isRetryable);
    console.error('Stack:', firebaseError.stack);
    console.groupEnd();
  }

  return firebaseError;
}

// Firebase operation wrapper with error handling
export async function withFirebaseErrorHandling<T>(
  operation: () => Promise<T>,
  context?: string
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw handleFirebaseError(error, context);
  }
}

// Retry wrapper for Firebase operations
export async function retryFirebaseOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  context?: string
): Promise<T> {
  let lastError: FirebaseApiError | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = handleFirebaseError(error, context);
      
      if (!lastError.isRetryable || attempt === maxRetries) {
        throw lastError;
      }
      
      // Exponential backoff
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}